import { useEffect, useState } from 'react';
import { ChevronDown, History } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const ago = (iso) => {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
};

/** Recent round sessions for one modality, so the progression is visible. */
export function RoundsHistory({ modalityId, testId = 'rounds-history' }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!modalityId) return;
    let cancelled = false;
    api.get('/cardio/rounds-history', { params: { modality_id: modalityId, limit: 4 } })
      .then((res) => { if (!cancelled) setData(res.data?.modalities?.[0] || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [api, modalityId]);

  if (!data?.sessions?.length) return null;

  const latest = data.sessions[0];
  return (
    <div className="mt-3 pt-3 border-t border-white/5" data-testid={testId}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 text-left"
        data-testid={`${testId}-toggle`}
      >
        <History size={12} className="text-accent shrink-0" />
        <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-white/60">
          Last {latest.rounds_completed ?? '—'}/{latest.rounds_prescribed} rounds
          {latest.rpe_reported ? ` · RPE ${latest.rpe_reported}` : ''} · {ago(latest.completed_at)}
        </span>
        <ChevronDown
          size={13}
          className={`ml-auto text-white/40 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="mt-2 space-y-1.5" data-testid={`${testId}-list`}>
          {data.sessions.map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-[11px]" data-testid={`${testId}-row-${i}`}>
              <span className="text-white/35 w-16 shrink-0">{ago(s.completed_at)}</span>
              <span className="font-heading font-bold text-white tabular-nums">
                {s.rounds_completed ?? '—'}/{s.rounds_prescribed}
              </span>
              <span className="text-white/30">rounds</span>
              {s.rpe_reported ? <span className="text-accent">RPE {s.rpe_reported}</span> : null}
              {s.minutes != null ? (
                <span className="text-white/30 ml-auto tabular-nums">
                  {s.minutes >= 1 ? `${Math.round(s.minutes)} min` : '<1 min'}
                </span>
              ) : null}
            </div>
          ))}
          {data.next_sets ? (
            <p className="pt-1.5 text-[11px] text-accent font-heading font-bold" data-testid={`${testId}-next`}>
              Next session starts at {data.next_sets} rounds
              {data.applied_delta ? ` (${data.applied_delta > 0 ? '+' : ''}${data.applied_delta} from your last effort)` : ''}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
